type BreakpointClassValueMap = Record<string, string>;

export type BreakpointClassMap = {
  default: BreakpointClassValueMap;
};

type BreakpointClassValue<TClassMap extends BreakpointClassMap> =
  Extract<keyof TClassMap["default"], string>;

export type BreakpointProp<
  TClassMap extends BreakpointClassMap,
  TValue extends BreakpointClassValue<TClassMap> = BreakpointClassValue<TClassMap>,
> =
  | TValue
  | {
    [TRequiredBreakpoint in Extract<keyof TClassMap, string>]: {
      [TKey in TRequiredBreakpoint]: TValue;
    } & {
      [TOptionalKey in Exclude<Extract<keyof TClassMap, string>, TRequiredBreakpoint>]?: TValue;
    };
  }[Extract<keyof TClassMap, string>];

export type BreakpointClassProp<TClassMap extends BreakpointClassMap> =
  BreakpointProp<TClassMap>;

const BREAKPOINT_ORDER = [ "default", "sm", "md", "lg", "xl", "2xl", "3xl" ] as const;

export const makeBreakpointClassName = <TClassMap extends BreakpointClassMap>(
  prop: BreakpointClassProp<TClassMap>,
  classMap: TClassMap,
  defaultValue?: Extract<keyof TClassMap["default"], string>,
): string => {
  if (typeof prop === "string") { return classMap.default[prop] ?? ""; }

  const defaultProp = prop.default ?? defaultValue;
  const classMapByBreakpoint = classMap as Record<string, BreakpointClassValueMap | undefined>;
  const propByBreakpoint = prop as Record<string, string | undefined>;

  return BREAKPOINT_ORDER
    .map((breakpoint) => {
      const value = breakpoint === "default" ? defaultProp : propByBreakpoint[breakpoint];

      if (!value) { return undefined; }

      return classMapByBreakpoint[breakpoint]?.[value];
    })
    .filter(Boolean)
    .join(" ");
};
